// src/screens/WalkingScreen.jsx
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoMdAdd } from "react-icons/io";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import PageTitle from "../components/PageTitleCompoent";
import WalkingCard from "../components/WalkingCard";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";

import "../css/WalkingScreen.css";
import api from "../api"; // ✅ centralized API instance

export default function WalkingScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasAddedRef = useRef(false);

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingPackageId, setEditingPackageId] = useState(null);
  const [editData, setEditData] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);

  const tagMap = {
    popular: "Popular",
    great_deals: "Great Deals",
    steal_deals: "Steal Deals",
  };

  // --- Fetch packages ---
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get("/walking-packages/");

        const mapped = res.data.map((pkg) => ({
          id: pkg.id,
          title: pkg.package_name,
          duration: pkg.duration,
          subscribedPrice: parseFloat(pkg.subscribed_user_offer_amount),
          unsubscribedPrice: parseFloat(pkg.package_amount),
          tag: pkg.tags || "",
        }));

        setPackages(mapped);
      } catch (err) {
        console.error("Failed to fetch walking packages:", err);
        setError("Failed to load packages.");
        toast.error("Failed to load packages.");
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  // --- Add new package from AddWalkingPackage screen ---
  useEffect(() => {
    if (location.state?.newPackage && !hasAddedRef.current) {
      const pkg = location.state.newPackage;
      setPackages((prev) => [
        ...prev,
        {
          id: pkg.id,
          title: pkg.package_name,
          duration: pkg.duration,
          subscribedPrice: parseFloat(pkg.subscribed_user_offer_amount),
          unsubscribedPrice: parseFloat(pkg.package_amount),
          tag: pkg.tags || "",
        },
      ]);
      hasAddedRef.current = true;
      navigate(location.pathname, { replace: true, state: {} });
      toast.success("New package added!");
    }
  }, [location.state, navigate, location.pathname]);

  // --- Delete package ---
  const openDeleteModal = (pkg) => {
    setPackageToDelete(pkg);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setPackageToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const confirmDelete = async () => {
    if (!packageToDelete) return;
    try {
      await api.delete(`/walking-packages/${packageToDelete.id}/`);
      setPackages((prev) => prev.filter((p) => p.id !== packageToDelete.id));
      closeDeleteModal();
      toast.info(`Deleted package "${packageToDelete.title}"`);
    } catch (err) {
      console.error("Failed to delete package:", err);
      toast.error("Failed to delete package. Try again.");
    }
  };

  // --- Edit package ---
  const handleEditClick = (pkg) => {
    setEditingPackageId(pkg.id);
    setEditData({ ...pkg });
  };

  const handleSave = async (updated) => {
    try {
      const payload = {
        package_name: updated.title,
        duration: updated.duration,
        package_amount: updated.unsubscribedPrice,
        subscribed_user_offer_amount: updated.subscribedPrice,
        tags: updated.tag,
      };

      const res = await api.patch(`/walking-packages/${editingPackageId}/`, payload);

      const saved = {
        id: res.data.id,
        title: res.data.package_name,
        duration: res.data.duration,
        subscribedPrice: parseFloat(res.data.subscribed_user_offer_amount),
        unsubscribedPrice: parseFloat(res.data.package_amount),
        tag: res.data.tags,
      };

      setPackages((prev) =>
        prev.map((p) => (p.id === saved.id ? saved : p))
      );

      setEditingPackageId(null);
      setEditData(null);
      toast.success(`Package "${saved.title}" updated successfully!`);
    } catch (err) {
      console.error("Failed to update package:", err);
      toast.error("Failed to update package. Try again.");
    }
  };

  const handleBack = () => navigate(-1);

  return (
    <div className="container">
      <Sidebar />
      <div className="main">
        <Header />
        <div className="main-content">
          <ToastContainer position="top-right" autoClose={3000} />
          <div className="heading-sec">
            <PageTitle title="Walking" onBack={handleBack} />
            <div className="add-package-sec">
              <button
                className="add-package-btn"
                onClick={() =>
                  navigate("/add_walking", { state: { type: "Walking" } })
                }
              >
                <IoMdAdd className="add-package-icon" color="#EBA8BF" />
                Add Package
              </button>
            </div>
          </div>

          {loading && <p>Loading packages...</p>}
          {error && <p style={{ color: "red" }}>{error}</p>}

          <div className="walking-card-grid">
            {packages.map((pkg) => (
              <WalkingCard
                key={pkg.id}
                item={pkg}
                tag={tagMap[pkg.tag] || pkg.tag}
                onAddService={() => handleEditClick(pkg)}
                onDelete={() => openDeleteModal(pkg)}
              />
            ))}
          </div>

          {editData && (
            <div className="modal-overlay">
              <div className="modal-content">
                <WalkingCard
                  item={editData}
                  isEditable={true}
                  tag={tagMap[editData.tag] || editData.tag}
                  onClose={() => {
                    setEditData(null);
                    setEditingPackageId(null);
                  }}
                  onSave={handleSave}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <DeleteWithConfirmation
        isOpen={isDeleteModalOpen}
        onCancel={closeDeleteModal}
        onConfirm={confirmDelete}
        title={`Delete ${packageToDelete?.title || "Package"}?`}
        message="Are you sure you want to delete this package? This action cannot be undone."
      />
    </div>
  );
}
